// ---------------------------------------------------------------------------
// Creative Engine API Hooks
// ---------------------------------------------------------------------------
// Shared hooks for all Creative Engine screens.
// Each hook wraps a specific api-pages endpoint with loading/error state.
// ---------------------------------------------------------------------------

import { useState, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import {
  analyzeOptimization,
  type OptimizationAnalysisResult,
  type OptimizationSeoInput,
} from "@ease/page-builder";
import {
  getAiCheckpoint,
  getAiCheckpointSections,
  modifyAiCheckpoint,
  rejectAiCheckpoint,
  useAiPageGeneration,
  type AiCheckpointDetail,
} from "@/hooks/use-page-ai";
import {
  canAutoApplyOptimizationRecommendation,
  type CompetitorDnaExtractionResult,
} from "@ease/page-builder/services";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface CopilotSuggestion {
  id: string;
  type: "conversion" | "seo" | "accessibility" | "performance";
  title: string;
  description: string;
  impact: "high" | "medium" | "low";
  autoFixAvailable: boolean;
}

interface CopilotResponse {
  conversionScore: number;
  seoScore: number;
  suggestions: CopilotSuggestion[];
  heatmapData: Array<{ componentId: string; attention: number }>;
}

interface FormAnalytics {
  totalSubmissions: number;
  completionRate: number;
  avgTimeSeconds: number;
  dropOffFunnel: Array<{ fieldName: string; reachedPercent: number; completedPercent: number }>;
  fieldCompletion: Array<{ fieldId: string; fieldName: string; completionRate: number }>;
  qualificationBreakdown: Array<{ segment: string; count: number; percent: number }>;
}

// ---------------------------------------------------------------------------
// Generic fetch hook
// ---------------------------------------------------------------------------

function useApi<T>() {
  const [state, setState] = useState<ApiState<T>>({ data: null, loading: false, error: null });

  const execute = useCallback(async (url: string, options?: RequestInit) => {
    setState({ data: null, loading: true, error: null });
    try {
      const response = await apiFetch<Record<string, unknown>>(url, options);
      const data = (
        response
        && typeof response === "object"
        && "data" in response
          ? response.data
          : response
      ) as T;
      setState({ data, loading: false, error: null });
      return data;
    } catch (err) {
      const message = err instanceof ApiError
        ? `${err.status}: ${err.message}`
        : err instanceof Error ? err.message : "Unknown error";
      setState({ data: null, loading: false, error: message });
      return null;
    }
  }, []);

  return { ...state, execute };
}

// ---------------------------------------------------------------------------
// Copilot Hook (C1)
// ---------------------------------------------------------------------------

export function useCopilot() {
  const [state, setState] = useState<ApiState<CopilotResponse>>({
    data: null,
    loading: false,
    error: null,
  });

  const analyze = useCallback(async (
    pageId: string,
    puckJson: unknown,
    seo?: OptimizationSeoInput,
  ) => {
    setState({ data: null, loading: true, error: null });

    try {
      const analysis = analyzeOptimization({
        pageId,
        components: extractCopilotComponents(puckJson),
        seo,
      });
      const response = toCopilotResponse(analysis);
      setState({ data: response, loading: false, error: null });
      return response;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Copilot analysis failed";
      setState({ data: null, loading: false, error: message });
      return null;
    }
  }, []);

  return { ...state, analyze };
}

// ---------------------------------------------------------------------------
// Form Analytics Hook (C6)
// ---------------------------------------------------------------------------

export function useFormAnalytics() {
  const api = useApi<FormAnalytics>();

  const load = useCallback(async (formId: string) => {
    return api.execute(`/crm/pages/forms/${encodeURIComponent(formId)}/analytics`);
  }, [api]);

  return { ...api, load };
}

// ---------------------------------------------------------------------------
// Competitor DNA Hook (C9)
// ---------------------------------------------------------------------------

export function useCompetitorDna() {
  const api = useApi<CompetitorDnaExtractionResult>();

  const extract = useCallback(async (url: string, input?: { siteId?: string }) => {
    return api.execute("/crm/pages/generate/competitor-dna", {
      method: "POST",
      body: JSON.stringify({
        url,
        ...(input?.siteId ? { siteId: input.siteId } : {}),
      }),
    });
  }, [api]);

  return { ...api, extract };
}

// ---------------------------------------------------------------------------
// Checkpoint-backed builder generation hook.
// Legacy name preserved so mounted builder screens can migrate without keeping
// the old fake streaming/api-pages route assumptions alive.
// ---------------------------------------------------------------------------

export function useStreamingGenerate() {
  const [checkpoint, setCheckpoint] = useState<AiCheckpointDetail | null>(null);
  const [blocks, setBlocks] = useState<Array<{ type: string; props: Record<string, unknown> }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { mutate: createCheckpoint } = useAiPageGeneration();

  const syncCheckpoint = useCallback(async (checkpointId: string) => {
    const detail = await getAiCheckpoint(checkpointId);
    setCheckpoint(detail);
    setBlocks(
      getAiCheckpointSections(detail).map((component) => ({
        type: component.type,
        props: component.props ?? {},
      })),
    );
    return detail;
  }, []);

  const cancel = useCallback(() => {
    setLoading(false);
  }, []);

  const generate = useCallback(async (
    prompt: string,
    options?: { pageType?: string; brandContext?: unknown },
  ) => {
    setBlocks([]);
    setCheckpoint(null);
    setLoading(true);
    setError(null);

    try {
      const created = await createCheckpoint({
        prompt,
        pageType: options?.pageType,
      });
      if (!created?.id) {
        throw new Error("Checkpoint creation did not return an id");
      }

      return await syncCheckpoint(created.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Generation failed");
      return null;
    } finally {
      setLoading(false);
    }
  }, [createCheckpoint, syncCheckpoint]);

  const refine = useCallback(async (instructions: string) => {
    if (!checkpoint?.id) {
      setError("No checkpoint available to refine");
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      await modifyAiCheckpoint(checkpoint.id, instructions);
      return await syncCheckpoint(checkpoint.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Refinement failed");
      return null;
    } finally {
      setLoading(false);
    }
  }, [checkpoint?.id, syncCheckpoint]);

  const reject = useCallback(async () => {
    if (!checkpoint?.id) {
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      await rejectAiCheckpoint(checkpoint.id);
      setCheckpoint(null);
      setBlocks([]);
      return null;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reject checkpoint");
      return null;
    } finally {
      setLoading(false);
    }
  }, [checkpoint?.id]);

  const refresh = useCallback(async () => {
    if (!checkpoint?.id) {
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      return await syncCheckpoint(checkpoint.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load checkpoint");
      return null;
    } finally {
      setLoading(false);
    }
  }, [checkpoint?.id, syncCheckpoint]);

  return { checkpoint, blocks, loading, error, generate, refine, reject, refresh, cancel };
}

export type {
  CopilotResponse,
  CopilotSuggestion,
  FormAnalytics,
  CompetitorDnaExtractionResult,
};

function extractCopilotComponents(puckJson: unknown) {
  if (!puckJson || typeof puckJson !== "object") {
    return [];
  }

  const candidate = puckJson as { content?: unknown };
  if (!Array.isArray(candidate.content)) {
    return [];
  }

  return candidate.content.flatMap((item) => {
    if (!item || typeof item !== "object") {
      return [];
    }

    const component = item as { type?: unknown; props?: unknown };
    if (typeof component.type !== "string" || component.type.length === 0) {
      return [];
    }

    return [{
      type: component.type,
      props: component.props && typeof component.props === "object"
        ? component.props as Record<string, unknown>
        : {},
    }];
  });
}

function toCopilotResponse(analysis: OptimizationAnalysisResult): CopilotResponse {
  return {
    conversionScore: analysis.conversion.total,
    seoScore: analysis.scores.seo,
    suggestions: analysis.recommendations.map((recommendation) => ({
      id: recommendation.id,
      type: recommendation.category === "conversion"
        ? "conversion"
        : recommendation.category === "seo"
          ? "seo"
          : recommendation.category === "performance"
            ? "performance"
            : "accessibility",
      title: recommendation.title,
      description: recommendation.description,
      impact: recommendation.impact,
      autoFixAvailable: canAutoApplyOptimizationRecommendation(recommendation.id),
    })),
    heatmapData: analysis.heatmap.map((item) => ({
      componentId: item.componentId ?? `${item.componentType}-${item.componentIndex}`,
      attention: item.attention,
    })),
  };
}
